import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Autocomplete, Stack, TextField, Typography } from '@mui/material'
import { SubmitButton } from '~/components'
import { useRouteContext } from '~/routes'
import { bshSlice } from '~/store'

const CopyParagraphsFrom = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { subCategory, product } = useRouteContext(CopyParagraphsFrom.name)
  const products = useSelector((state) =>
    bshSlice.products(state, subCategory.products)
  )
  const categoryParagraphs = useSelector(bshSlice.categoryParagraphs)
  const productParagraphs = useSelector(bshSlice.productParagraphs)
  const isGeneratingParagraphs = useSelector((state) =>
    bshSlice.isPendingRequest(state, ['generateParagraphs'])
  )
  const isRemovingParagraphs = useSelector((state) =>
    bshSlice.isPendingRequest(state, ['removeParagraphs'])
  )
  const isGettingProductParagraphs = useSelector((state) =>
    bshSlice.isPendingRequest(state, ['getProductParagraphs'])
  )
  const isCopyingParagraphs = useSelector((state) =>
    bshSlice.isPendingRequest(state, ['copyParagraphsFrom'])
  )

  const [source, setSource] = useState(subCategory)
  const selectedProduct = useMemo(
    () => (source?.model === 'product' ? source : undefined),
    [source]
  )
  const [paragraph, setParagraph] = useState()

  const paragraphs = useMemo(
    () =>
      (selectedProduct ? productParagraphs : categoryParagraphs)?.filter(
        ({ name }) => !!name
      ),
    [categoryParagraphs, productParagraphs, selectedProduct]
  )
  const noParagraphs = useMemo(
    () => isGettingProductParagraphs || paragraphs?.length === 0,
    [isGettingProductParagraphs, paragraphs?.length]
  )

  const copyable = useMemo(
    () => !(noParagraphs || isGettingProductParagraphs),
    [isGettingProductParagraphs, noParagraphs]
  )

  useEffect(() => {
    if (selectedProduct) {
      dispatch(
        bshSlice.getProductParagraphs({
          data: { product: selectedProduct },
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct])

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        justifyContent: 'space-between',
        alignItems: 'baseline',
        backgroundColor: 'background.default',
        py: 2,
      }}
    >
      <Stack direction="row" spacing={0.5}>
        <SubmitButton
          loading={isGeneratingParagraphs}
          label={t('paragraph.copy.generate')}
          size="small"
          variant="contained"
          onClick={() =>
            dispatch(bshSlice.generateParagraphs({ data: { product } }))
          }
        />
        <SubmitButton
          label={t('paragraph.copy.remove-all')}
          loading={isRemovingParagraphs}
          size="small"
          variant="contained"
          onClick={() =>
            dispatch(bshSlice.removeParagraphs({ data: { product } }))
          }
        />
      </Stack>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'baseline' }}>
        <Typography variant="annotation">
          {t('paragraph.copy.from')}:
        </Typography>
        <Stack spacing={0.5}>
          <Autocomplete
            disableClearable
            getOptionDisabled={(option) => option.id === product.id}
            getOptionKey={(option) => option?.id}
            getOptionLabel={(option) => option?.name || subCategory.name}
            groupBy={(option) => t(`paragraph.copy.from-${option.model}`)}
            options={[
              subCategory,
              ...products.map((p) => ({ ...p, disabled: true })),
            ]}
            renderInput={(params) => (
              <TextField {...params} variant="standard" />
            )}
            sx={{ minWidth: 280, maxWidth: '100%' }}
            value={source || ''}
            onChange={(e, value) => {
              setSource(value)
              setParagraph()
            }}
          />
          <Autocomplete
            disableClearable={!paragraph}
            disabled={noParagraphs || isGettingProductParagraphs}
            getOptionKey={(option) => option?.id}
            getOptionLabel={(option) => option?.name || ''}
            options={paragraphs}
            placeholder="Combo box"
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  noParagraphs ? 'Brak paragrafów' : 'Wszystkie paragrafy'
                }
                variant="standard"
              />
            )}
            value={paragraph || ''}
            onChange={(e, value) => setParagraph(value)}
          />
        </Stack>
        <SubmitButton
          disabled={!copyable}
          label={t('ui.copy')}
          loading={isCopyingParagraphs}
          size="small"
          sx={{ alignSelf: 'flex-end' }}
          variant="contained"
          onClick={() =>
            dispatch(
              bshSlice.copyParagraphsFrom({
                data: {
                  product,
                  source: paragraph ?? source ?? subCategory,
                },
              })
            )
          }
        />
      </Stack>
    </Stack>
  )
}

export default CopyParagraphsFrom
